import * as React from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export interface CurrencySmartInputProps
  extends Omit<
    React.ComponentProps<'input'>,
    'type' | 'value' | 'onChange' | 'inputMode'
  > {
  locale?: string;
  // External value in minor units (e.g., cents). Use integer for safety.
  value?: number;
  // Emits minor units integer (e.g., cents)
  onValueChange?: (minorUnits: number) => void;
  // Fraction digits (default 2); set 0 for JPY, etc.
  fractionDigits?: number;
  // Limit digits in the internal buffer (excluding decimal point)
  maxDigits?: number;
  currencySymbol?: string;
}

// WIP
export function CurrencySmartInput({
  className,
  locale = 'de-DE',
  value: externalMinor,
  onValueChange,
  onFocus,
  fractionDigits = 2,
  maxDigits = 18,
  currencySymbol = '€',
  ...props
}: CurrencySmartInputProps) {
  const format = React.useCallback(
    (minor: number) => {
      const denom = 10 ** fractionDigits;
      const major = minor / denom;
      return new Intl.NumberFormat(locale, {
        style: 'decimal',
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
        useGrouping: true,
      }).format(major);
    },
    [locale, fractionDigits],
  );

  // Internal digit buffer as a string of digits (minor units)
  const [digits, setDigits] = React.useState<string>(() => {
    const initial = typeof externalMinor === 'number' ? externalMinor : 0;
    return String(initial);
  });

  // Derived display string
  const display = React.useMemo(() => {
    const minor = parseInt(digits || '0', 10) || 0;
    if (minor === 0 && digits.length === 0) return '';
    return format(minor);
  }, [digits, format]);

  // Sync external value -> internal digits only when it changes
  React.useEffect(() => {
    if (typeof externalMinor === 'number') {
      const asStr = String(externalMinor);
      if (asStr !== digits) {
        setDigits(asStr);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalMinor]);

  const inputRef = React.useRef<HTMLInputElement | null>(null);

  function sanitizeDigits(raw: string): string {
    // Keep digits only, trim leading zeros but preserve single "0"
    const only = raw.replace(/\D/g, '').slice(0, maxDigits);
    return only.replace(/^0+(?=\d)/, '');
  }

  function emit(minorStr: string) {
    const clean = minorStr === '' ? '0' : minorStr;
    const minor = parseInt(clean, 10) || 0;
    setDigits(clean);
    onValueChange?.(minor);
  }

  function handleFocus(e: React.FocusEvent<HTMLInputElement>) {
    // Move caret to end for simple “type-at-end” UX
    const el = e.currentTarget;
    requestAnimationFrame(() => {
      const len = el.value.length;
      el.setSelectionRange(len, len);
    });
    onFocus?.(e);
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value;
    const nextDigits = sanitizeDigits(raw);
    emit(nextDigits);

    // Keep caret at the end
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      const len = el.value.length;
      el.setSelectionRange(len, len);
    });
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    const allowed = new Set([
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'Home',
      'End',
      'Tab',
      'Enter',
    ]);
    if (allowed.has(e.key) || e.metaKey || e.ctrlKey) return;

    if (!/^\d$/.test(e.key)) {
      e.preventDefault();
    }
  }

  function handlePaste(e: React.ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text') ?? '';
    const nextDigits = sanitizeDigits(pasted);
    emit(nextDigits);

    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      const len = el.value.length;
      el.setSelectionRange(len, len);
    });
  }

  return (
    <div className="tw:relative tw:flex tw:items-center tw:gap-2">
      <span
        className={cn(
          'tw:absolute tw:right-1 tw:top-1/2 tw:-translate-y-1/2 tw:size-5 tw:text-neutral-600',
          props?.disabled && 'tw:opacity-50',
        )}
      >
        {currencySymbol}
      </span>
      <Input
        ref={inputRef}
        type="text"
        className={cn('tw:pr-8', className)}
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={24}
        onFocus={handleFocus}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        value={display}
        {...props}
      />
    </div>
  );
}
